import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { 
  FileSearch, 
  Upload,
  Zap,
  CheckCircle,
  AlertTriangle,
  Euro,
  Clock,
  Shield,
  ArrowRight,
  BadgeCheck,
  FileWarning,
  Calculator
} from 'lucide-react';
import { Button } from '../../components/ui/button';

export default function FeatureAnalyzeQuote() {
  const navigate = useNavigate();
  const { user } = useAuth();

  const handleCTA = () => {
    navigate(user ? '/analyze' : '/auth');
  };

  return (
    <div className="min-h-screen bg-black-rich pt-20">
      {/* SEO Meta Tags */}
      <Helmet>
        <title>Analyser un Devis Travaux en Ligne | Vérification Gratuite | Assistant by Léon</title>
        <meta name="description" content="Vérifiez votre devis de rénovation avant de signer. Détectez les prix gonflés, les postes manquants et les anomalies. Analyse IA gratuite en 30 secondes." />
        <meta name="keywords" content="analyser devis travaux, vérifier devis artisan, devis rénovation, prix travaux, analyse devis gratuit, devis maison" />
        <link rel="canonical" href="https://assistantbyleon.fr/fonctionnalite/analyse-devis" />
        <meta property="og:title" content="Analyser un Devis Travaux - Vérification Gratuite en 30s" />
        <meta property="og:description" content="Détectez les anomalies dans vos devis de rénovation avec l'IA. Évitez les mauvaises surprises avant de payer votre artisan." />
        <meta property="og:type" content="website" />
      </Helmet>
      
      {/* Section 1: Accroche SEO optimisée */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-500/10 border border-blue-500/20 rounded-full mb-6"
          >
            <FileSearch className="w-5 h-5 text-blue-400" />
            <span className="text-blue-400 font-medium">Analyse de devis en ligne</span>
          </motion.div>
          
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-4xl sm:text-5xl font-heading font-bold text-white mb-6"
          >
            Vérifiez votre devis travaux{' '}
            <span className="text-gold-500">avant de signer</span>
          </motion.h1>
          
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-xl text-zinc-400 max-w-2xl mx-auto"
          >
            Détectez les <strong className="text-white">prix gonflés</strong>, les <strong className="text-white">postes manquants</strong> et les <strong className="text-white">anomalies</strong> dans votre devis de rénovation.
            <span className="block mt-2 text-gold-400">Analyse gratuite par intelligence artificielle en 30 secondes.</span>
          </motion.p>
        </div>
      </section>

      {/* Section 2: Comment analyser un devis - 3 étapes */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-zinc-900/50">
        <div className="max-w-4xl mx-auto">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-2xl font-heading font-bold text-white text-center mb-4"
          >
            Comment vérifier un devis d'artisan ?
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-zinc-400 text-center mb-12 max-w-2xl mx-auto"
          >
            Léon analyse votre devis de rénovation en 3 étapes simples. Fini les heures à comparer les prix sur internet.
          </motion.p>

          <div className="grid md:grid-cols-3 gap-6">
            {[
              { 
                step: '1', 
                title: 'Importez votre devis', 
                desc: 'Uploadez le PDF ou prenez une photo du devis de votre artisan.',
                icon: Upload,
                color: 'blue'
              },
              { 
                step: '2', 
                title: 'Analyse IA instantanée', 
                desc: 'Notre intelligence artificielle vérifie chaque ligne en 30 secondes.',
                icon: Zap,
                color: 'gold'
              },
              { 
                step: '3', 
                title: 'Rapport détaillé', 
                desc: 'Recevez un diagnostic complet avec les points à négocier.',
                icon: CheckCircle,
                color: 'emerald'
              }
            ].map((item, index) => (
              <motion.div
                key={item.step}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="text-center"
              >
                <div className={`w-16 h-16 mx-auto mb-4 rounded-2xl bg-${item.color}-500/10 border border-${item.color}-500/20 flex items-center justify-center`}>
                  <item.icon className={`w-8 h-8 text-${item.color}-400`} />
                </div>
                <div className="text-gold-500 font-bold text-sm mb-2">Étape {item.step}</div>
                <h3 className="text-white font-bold text-lg mb-2">{item.title}</h3>
                <p className="text-zinc-500 text-sm">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Section 3: Exemple réel d'analyse de devis */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-2xl font-heading font-bold text-white text-center mb-8"
          >
            Exemple : ce que Léon détecte dans un devis rénovation salle de bain
          </motion.h2>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="glass-card p-8 border-blue-500/20"
          >
            <div className="flex items-start gap-4 mb-6">
              <div className="w-12 h-12 rounded-xl bg-blue-500/20 flex items-center justify-center flex-shrink-0">
                <FileWarning className="w-6 h-6 text-blue-400" />
              </div>
              <div>
                <h3 className="text-white font-bold text-lg mb-1">Analyse d'un devis de 12 000€</h3>
                <p className="text-zinc-500 text-sm">Rénovation complète salle de bain</p>
              </div>
            </div>
            
            <div className="space-y-4">
              <p className="text-zinc-300 leading-relaxed">
                Un particulier nous soumet un devis pour rénover sa salle de bain. Voici ce que Léon a détecté :
              </p>
              <ul className="space-y-3">
                <li className="flex items-start gap-3 p-3 bg-red-500/10 rounded-lg border border-red-500/20">
                  <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <span className="text-white font-semibold">Poste manquant critique</span>
                    <p className="text-zinc-400 text-sm">L'étanchéité sous carrelage n'est pas mentionnée - risque d'infiltration à terme</p>
                  </div>
                </li>
                <li className="flex items-start gap-3 p-3 bg-amber-500/10 rounded-lg border border-amber-500/20">
                  <Calculator className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <span className="text-white font-semibold">Prix au-dessus du marché</span>
                    <p className="text-zinc-400 text-sm">Carrelage facturé 85€/m² - prix moyen constaté : 55-65€/m²</p>
                  </div>
                </li>
                <li className="flex items-start gap-3 p-3 bg-emerald-500/10 rounded-lg border border-emerald-500/20">
                  <BadgeCheck className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <span className="text-white font-semibold">Point positif</span>
                    <p className="text-zinc-400 text-sm">Main d'œuvre plomberie correctement chiffrée selon les standards</p>
                  </div>
                </li>
              </ul>
              <div className="mt-6 p-4 bg-gold-500/10 rounded-lg border border-gold-500/30">
                <p className="text-gold-400 font-semibold text-lg mb-1">
                  Économie potentielle identifiée : 1 500€
                </p>
                <p className="text-zinc-400 text-sm">
                  + un problème technique évité grâce à la détection du poste manquant
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Section 4: Pourquoi faire vérifier son devis */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-zinc-900/50">
        <div className="max-w-4xl mx-auto">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-2xl font-heading font-bold text-white text-center mb-4"
          >
            Pourquoi faire analyser votre devis avant de signer ?
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-zinc-400 text-center mb-10 max-w-2xl mx-auto"
          >
            Un devis mal vérifié peut coûter des milliers d'euros. Léon vous aide à prendre la bonne décision.
          </motion.p>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { icon: AlertTriangle, title: 'Détectez les oublis', desc: 'Postes manquants qui coûteront cher plus tard', color: 'red' },
              { icon: Euro, title: 'Comparez les prix', desc: 'Identifiez les tarifs au-dessus du marché', color: 'emerald' },
              { icon: Clock, title: 'Gagnez du temps', desc: 'Analyse complète en 30 secondes vs des heures de recherche', color: 'blue' },
              { icon: Shield, title: 'Signez sereinement', desc: 'Engagez-vous avec un artisan en toute confiance', color: 'gold' }
            ].map((item, index) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="glass-card-dark p-5 text-center"
              >
                <div className={`w-12 h-12 mx-auto mb-3 rounded-xl bg-${item.color}-500/10 flex items-center justify-center`}>
                  <item.icon className={`w-6 h-6 text-${item.color}-400`} />
                </div>
                <h3 className="text-white font-bold mb-1">{item.title}</h3>
                <p className="text-zinc-500 text-xs">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Section 5: CTA Final */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="max-w-xl mx-auto text-center"
        >
          <h2 className="text-2xl sm:text-3xl font-heading font-bold text-white mb-4">
            Prêt à vérifier votre devis ?
          </h2>
          <p className="text-zinc-400 mb-8">
            Uploadez votre devis PDF et obtenez un diagnostic complet en 30 secondes.
            <span className="block text-gold-400 mt-2">Gratuit pour votre premier devis.</span>
          </p>
          
          <Button
            onClick={handleCTA}
            className="px-10 py-6 text-lg bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-400 hover:to-blue-500 text-white font-bold"
            data-testid="cta-analyze-quote"
          >
            <FileSearch className="w-5 h-5 mr-2" />
            Analyser mon devis gratuitement
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
          
          <p className="text-zinc-600 text-xs mt-4">
            Gratuit • Sans engagement • Résultat en 30 secondes
          </p>
        </motion.div>
      </section>

    </div>
  );
}
